import type { ApiItem, Status } from "../utils/applicationCards";
import { deadlineChip, lastUpdate, oaDoneAt, oaDoneTitle, stageDeadline } from "../utils/home";
import {
  DEADLINE_TAG,
  ApplicationEditForm,
  ApplicationMenu,
  DeleteApplicationDialog,
  OUTLINE_TAG,
  STATUS_TAG,
  TAG,
  formatAppDate,
  useApplicationEditor,
} from "./applicationShared";
import { cn } from "../lib/utils";

type CardProps = {
  item: ApiItem;
  status: Status;
  onUpdate: () => void;
  readOnly?: boolean; // archived cards: no menu, so no edit / delete
  /** Open the emails panel for this application. */
  onOpen?: (item: ApiItem) => void;
  /** Starts dragging the card to another column (not while it's being edited) */
  onDragPointerDown?: (e: React.PointerEvent<HTMLElement>) => void;
  /** This card is being dragged: it leaves a dashed gap */
  placeholder?: boolean;
};

// One application on the tracker board.
export default function Card({ item, status, onUpdate, readOnly = false, onOpen, onDragPointerDown, placeholder = false }: CardProps) {
  const editor = useApplicationEditor(item, status, onUpdate);
  const tag = STATUS_TAG[status];
  const date = formatAppDate(lastUpdate(item));
  const deadline = readOnly ? null : stageDeadline(item);
  // An OA the user finished shows that instead of its due date
  const done = readOnly ? null : oaDoneAt(item);

  return (
    <>
      <div
        {...(onOpen && !editor.isEditing
          ? {
              role: "button",
              tabIndex: 0,
              "aria-label": `Show emails for ${item.company}`,
              onClick: () => onOpen(item),
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onOpen(item);
                }
              },
            }
          : {})}
        data-card-id={item.id}
        {...(onDragPointerDown && !readOnly && !editor.isEditing ? { onPointerDown: onDragPointerDown } : {})}
        className={cn(
          onOpen && !editor.isEditing && "cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-melt-caramel/30",
          "group rounded-[14px] border border-melt-text1/[.12] bg-melt-paper p-3.5 font-grotesk text-melt-text1 shadow-[0_1px_0_rgba(92,52,23,.04)] transition-[border-color,box-shadow,transform] duration-150",
          !editor.isEditing &&
            "hover:-translate-y-px hover:border-melt-text1/20 hover:shadow-[0_12px_24px_-18px_rgba(92,52,23,.45)]",
          placeholder &&
            "translate-y-0 border-dashed border-melt-text1/[.22] bg-transparent shadow-none hover:translate-y-0 hover:border-melt-text1/[.22] hover:shadow-none [&>*]:invisible"
        )}
      >
        {editor.isEditing ? (
          <ApplicationEditForm editor={editor} />
        ) : (
          <>
            <div className="flex items-center gap-2">
              <h3 className="min-w-0 flex-1 truncate text-[15px] font-medium leading-tight">{item.company || "Unknown"}</h3>
              {date && <span className="flex-none whitespace-nowrap font-plex text-[11px] text-melt-text3">{date}</span>}
              {!readOnly && (
                // stopPropagation: the ⋯ menu shouldn't also open the emails panel
                <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className="contents">
                <ApplicationMenu
                  company={item.company}
                  onEdit={editor.startEdit}
                  onDelete={() => editor.setShowDelete(true)}
                  triggerClassName="-mr-1"
                />
                </span>
              )}
            </div>

            {item.role && (
              // Two lines at most, so a bad title can't stretch the card
              <p title={item.role} className="mt-0.5 line-clamp-2 text-[13px] [overflow-wrap:anywhere] leading-[1.4] text-melt-text2">
                {item.role}
              </p>
            )}

            <div className="mt-[11px] flex flex-wrap gap-1.5">
              <span className={cn(TAG, tag.className)}>{tag.label}</span>
              {done ? (
                <span className={OUTLINE_TAG} title={oaDoneTitle(done)}>Done</span>
              ) : (
                deadline && <span className={DEADLINE_TAG}>{deadlineChip(deadline)}</span>
              )}
              {item.szn && <span className={OUTLINE_TAG}>{item.szn}</span>}
              {item.lctn && <span className={OUTLINE_TAG}>{item.lctn}</span>}
            </div>
          </>
        )}
      </div>

      <DeleteApplicationDialog
        open={editor.showDelete}
        onOpenChange={editor.setShowDelete}
        company={item.company}
        onConfirm={editor.remove}
        deleting={editor.deleting}
      />
    </>
  );
}
